import { Col, Row } from "antd";
import dayjs from "dayjs";
import { get, set } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdOutlineAddRoad,
  MdOutlineBadge,
  MdOutlineCake,
  MdOutlineMap,
  MdPeopleOutline,
} from "react-icons/md";
import Form from "shared/components/Form";
import FormField from "shared/components/FormField";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import useZipCodeCities from "shared/hooks/useZipCodeCities";
import { NAME_SPACES } from "shared/locales/constants";
import {
  BIRTH_COUNTRY_PARAMS,
  CONTACT_POINT_SYSTEM,
  DATE_FORMAT,
  GENDER,
  SUBSCRIPTION_FORM_KEYS,
} from "utils/constants";
import { isMajor } from "utils/helpers/date";

const Adherent = ({
  setPercent,
  isFieldRequired,
  initialValues,
  bindField,
  polynesiecities,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("ANSET_SUBSCRIPTION", { returnObjects: true });
  const birthZipCodePath = `telecoms.contact.${CONTACT_POINT_SYSTEM.BIRTH_ZIP_CODE}.value`;
  const [birthZipCode, setBirthZipCode] = useState(() =>
    get(initialValues, birthZipCodePath),
  );
  const { cities: birthZipCodeCities, loading: citiesLoading } =
    useZipCodeCities(birthZipCode);
  const cities = birthZipCodeCities.concat(
    polynesiecities
      .filter(({ zipCode }) => zipCode === birthZipCode)
      .map(({ city }) => ({
        label: city,
        value: city,
      })),
  );

  const BIRTH_COUNTRY = {
    [BIRTH_COUNTRY_PARAMS.POLYNESIE_FRANCAISE]: (value) => {
      set(
        initialValues,
        `telecoms.contact.${CONTACT_POINT_SYSTEM.BIRTH_COUNTRY}.value`,
        value,
      );
    },
    [BIRTH_COUNTRY_PARAMS.OTHER]: () => {
      set(
        initialValues,
        `telecoms.contact.${CONTACT_POINT_SYSTEM.BIRTH_COUNTRY}.value`,
        null,
      );
    },
  };

  const genderPath = "project.contact.user.gender";
  const genderField = bindField(genderPath, {
    label: SUBSCRIPTION.FORM.GENDER.LABEL,
    required: isFieldRequired(genderPath),
    onChange: () => setPercent(initialValues),
  });

  const lastnamePath = "project.contact.user.lastname";
  const lastnameField = bindField(lastnamePath, {
    label: SUBSCRIPTION.FORM.LAST_NAME.LABEL,
    required: isFieldRequired(lastnamePath),
    onBlur: () => setPercent(initialValues),
  });

  const birthNamePath = "project.contact.user.birthName";
  const birthNameField = bindField(birthNamePath, {
    label: SUBSCRIPTION.FORM.BIRTH_NAME.LABEL,
    required: isFieldRequired(birthNamePath),
    onBlur: () => setPercent(initialValues),
  });

  const firstnamePath = "project.contact.user.firstname";
  const firstnameField = bindField(firstnamePath, {
    label: SUBSCRIPTION.FORM.FIRST_NAME.LABEL,
    required: isFieldRequired(firstnamePath),
    onBlur: () => setPercent(initialValues),
  });

  const birthDatePath = "project.contact.user.birthDate";
  const birthDateField = bindField(birthDatePath, {
    label: SUBSCRIPTION.FORM.BIRTH_DATE.LABEL,
    required: isFieldRequired(birthDatePath),
    onChange: () => setPercent(initialValues),
    onBlur: () => setPercent(initialValues),
  });

  const departmentPath = `telecoms.contact.${CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH}.value`;
  const departmentField = bindField(departmentPath, {
    label: SUBSCRIPTION.FORM.DEPARTMENT_OF_BIRTH.LABEL,
    required: isFieldRequired(departmentPath),
    onBlur: () => setPercent(initialValues),
  });

  const birthZipCodeField = bindField(birthZipCodePath, {
    label: SUBSCRIPTION.FORM.BIRTH_ZIP_CODE.LABEL,
    required: isFieldRequired(birthZipCodePath),
    onChange: (value) => setBirthZipCode(value),
    onBlur: () => setPercent(initialValues),
  });

  const birthCityPath = `telecoms.contact.${CONTACT_POINT_SYSTEM.BIRTH_CITY}.value`;
  const birthCityField = bindField(birthCityPath, {
    label: SUBSCRIPTION.FORM.BIRTH_CITY.LABEL,
    required: isFieldRequired(birthCityPath),
    onChange: () => setPercent(initialValues),
    onBlur: () => setPercent(initialValues),
  });

  const birthCountrySelectPath = "birthCountry";
  const birthCountrySelectField = bindField(birthCountrySelectPath, {
    label: SUBSCRIPTION.FORM.BIRTH_COUNTRY.LABEL,
    required: isFieldRequired(birthCountrySelectPath),
    onChange: (value) => {
      BIRTH_COUNTRY[value]?.(value);
      setPercent(initialValues);
    },
    onBlur: () => setPercent(initialValues),
  });

  const birthCountryTextPath = `telecoms.contact.${CONTACT_POINT_SYSTEM.BIRTH_COUNTRY}.value`;
  const birthCountryTextField = bindField(birthCountryTextPath, {
    label: SUBSCRIPTION.FORM.OTHER_BIRTH_COUNTRY.LABEL,
    required: isFieldRequired(birthCountryTextPath),
    onBlur: () => setPercent(initialValues),
  });

  return (
    <div
      className="subscription-form--wrapper"
      id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.ADHERENT}-section`}
    >
      <Form
        type="vertical"
        title={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.ADHERENT.TITLE}
        subtitle={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.ADHERENT.SUBTITLE}
      >
        <Row gutter={[16, 16]}>
          <Col lg={24} xs={24}>
            <FormField
              path={genderField.path}
              label={genderField.label}
              required={genderField.required}
              disabled={genderField.disabled}
            >
              {(field) => (
                <Input
                  type="radio"
                  variant="group"
                  values={[
                    {
                      value: GENDER.MALE,
                      label: SUBSCRIPTION.FORM.GENDER.OPTIONS.MR,
                    },
                    {
                      value: GENDER.FEMALE,
                      label: SUBSCRIPTION.FORM.GENDER.OPTIONS.MRS,
                    },
                  ]}
                  icon={<MdPeopleOutline icon="man" size={16} />}
                  label={genderField.label}
                  value={get(initialValues, genderPath)}
                  {...field}
                  onChange={genderField.onChange}
                  onBlur={genderField.onBlur}
                />
              )}
            </FormField>
          </Col>
          <Col lg={24} xs={24}>
            <FormField
              path={lastnameField.path}
              label={lastnameField.label}
              required={lastnameField.required}
              disabled={lastnameField.disabled}
            >
              {(field) => (
                <Input
                  placeholder={SUBSCRIPTION.FORM.LAST_NAME.PLACEHOLDER}
                  label={lastnameField.label}
                  icon={<MdOutlineBadge size={16} />}
                  defaultValue={lastnameField.defaultValue}
                  {...field}
                  onChange={lastnameField.onChange}
                  onBlur={lastnameField.onBlur}
                />
              )}
            </FormField>
          </Col>
          <Col lg={24} xs={24}>
            <FormField
              path={birthNameField.path}
              label={birthNameField.label}
              required={birthNameField.required}
              disabled={birthNameField.disabled}
            >
              {(field) => (
                <Input
                  placeholder={SUBSCRIPTION.FORM.BIRTH_NAME.PLACEHOLDER}
                  label={birthNameField.label}
                  icon={<MdOutlineBadge size={16} />}
                  defaultValue={birthNameField.defaultValue}
                  {...field}
                  onChange={birthNameField.onChange}
                  onBlur={birthNameField.onBlur}
                  optional={!birthNameField.required}
                />
              )}
            </FormField>
          </Col>
          <Col lg={24} xs={24}>
            <FormField
              path={firstnameField.path}
              label={firstnameField.label}
              required={firstnameField.required}
              disabled={firstnameField.disabled}
            >
              {(field) => (
                <Input
                  label={firstnameField.label}
                  icon={<MdOutlineBadge size={16} />}
                  placeholder={SUBSCRIPTION.FORM.FIRST_NAME.PLACEHOLDER}
                  defaultValue={firstnameField.defaultValue}
                  {...field}
                  onChange={firstnameField.onChange}
                  onBlur={firstnameField.onBlur}
                />
              )}
            </FormField>
          </Col>
          <Col lg={24} xs={24}>
            <FormField
              path={birthDateField.path}
              label={birthDateField.label}
              required={birthDateField.required}
              disabled={birthDateField.disabled}
            >
              {(field) => (
                <Input
                  icon={<MdOutlineCake size={22} />}
                  label={birthDateField.label}
                  placeholder={SUBSCRIPTION.FORM.BIRTH_DATE.PLACEHOLDER}
                  type="date"
                  variant="simple"
                  format={DATE_FORMAT}
                  disabledDate={(current) => current > isMajor}
                  defaultPickerValue={dayjs().subtract(30, "years")}
                  defaultValue={
                    get(initialValues, birthDatePath)
                      ? dayjs(get(initialValues, birthDatePath))
                      : null
                  }
                  value={
                    get(initialValues, birthDatePath)
                      ? dayjs(get(initialValues, birthDatePath))
                      : null
                  }
                  {...field}
                  onChange={birthDateField.onChange}
                  onBlur={birthDateField.onBlur}
                />
              )}
            </FormField>
          </Col>
          <Col lg={12} xs={12}>
            <FormField
              path={departmentField.path}
              label={departmentField.label}
              required={departmentField.required}
              disabled={departmentField.disabled}
            >
              {(field) => (
                <Input
                  label={departmentField.label}
                  icon={<MdOutlineAddRoad size={16} />}
                  placeholder={SUBSCRIPTION.FORM.DEPARTMENT_OF_BIRTH.PLACEHOLDER}
                  defaultValue={departmentField.defaultValue}
                  {...field}
                  onChange={departmentField.onChange}
                  onBlur={departmentField.onBlur}
                />
              )}
            </FormField>
          </Col>
          <Col lg={12} xs={12}>
            <FormField
              path={birthZipCodeField.path}
              label={birthZipCodeField.label}
              required={birthZipCodeField.required}
              disabled={birthZipCodeField.disabled}
            >
              {(field) => (
                <Input
                  label={birthZipCodeField.label}
                  icon={<MdOutlineAddRoad size={16} />}
                  placeholder={SUBSCRIPTION.FORM.BIRTH_ZIP_CODE.PLACEHOLDER}
                  defaultValue={birthZipCodeField.defaultValue}
                  {...field}
                  onChange={birthZipCodeField.onChange}
                  onBlur={birthZipCodeField.onBlur}
                />
              )}
            </FormField>
          </Col>
          <Col lg={12}>
            <FormField
              path={birthCityField.path}
              label={birthCityField.label}
              required={birthCityField.required}
              disabled={birthCityField.disabled}
            >
              {(field) => (
                <Select
                  icon={<MdOutlineMap size={16} />}
                  label={birthCityField.label}
                  placeholder={SUBSCRIPTION.FORM.BIRTH_CITY.PLACEHOLDER}
                  showSearch
                  loading={citiesLoading}
                  options={cities}
                  defaultValue={birthCityField.defaultValue}
                  {...field}
                  onChange={birthCityField.onChange}
                  onBlur={birthCityField.onBlur}
                />
              )}
            </FormField>
          </Col>
          <Col lg={get(initialValues, birthCountrySelectPath) === "OTHER" ? 6 : 12}>
            <FormField
              path={birthCountrySelectField.path}
              label={birthCountrySelectField.label}
              required={birthCountrySelectField.required}
              disabled={birthCountrySelectField.disabled}
            >
              {(field) => (
                <Select
                  icon={<MdOutlineMap size={16} />}
                  label={birthCountrySelectField.label}
                  placeholder={SUBSCRIPTION.FORM.BIRTH_COUNTRY.PLACEHOLDER}
                  showSearch
                  options={Object.entries(SUBSCRIPTION.FORM.COUNTRIES).map(
                    ([value, label]) => ({ value, label }),
                  )}
                  defaultValue={birthCountrySelectField.defaultValue}
                  {...field}
                  onChange={birthCountrySelectField.onChange}
                  onBlur={birthCountrySelectField.onBlur}
                  optional={!birthCountrySelectField.required}
                />
              )}
            </FormField>
          </Col>
          {get(initialValues, birthCountrySelectPath) === "OTHER" && (
            <Col lg={6}>
              <FormField
                path={birthCountryTextField.path}
                label={birthCountryTextField.label}
                required={birthCountryTextField.required}
                disabled={birthCountryTextField.disabled}
              >
                {(field) => (
                  <Input
                    placeholder={
                      SUBSCRIPTION.FORM.OTHER_BIRTH_COUNTRY.PLACEHOLDER
                    }
                    label={birthCountryTextField.label}
                    icon={<MdOutlineMap size={16} />}
                    defaultValue={birthCountryTextField.defaultValue}
                    {...field}
                    onChange={birthCountryTextField.onChange}
                    onBlur={birthCountryTextField.onBlur}
                  />
                )}
              </FormField>
            </Col>
          )}
        </Row>
      </Form>
    </div>
  );
};

export default Adherent;
